export const announcement = {
    badge: "Fast 500",
    text: "Ranked #212 on the Deloitte Fast 500 with 640% growth",
    link: "Read more"
}

export const heroExamples = [
    "Sync Shopify orders into NetSuite",
    "Post new HubSpot deals to Slack",
    "Reconcile Stripe payouts in QuickBooks",
    "Create Zendesk tickets from Typeform responses",
    "Add Calendly bookings to Salesforce"
]

export const customerLogos = [
    "Northgate",
    "Halden & Co",
    "Brightline",
    "Kestrel Freight",
    "Oakmont Supply",
    "Pallet",
    "Lumen",
    "Arbor Goods",
    "Tessel",
    "Vantage Field",
    "Ridgeway",
    "Copperleaf",
    "Sable Coffee",
    "Mosaic Health",
    "Quarry",
    "Helix Parts"
]

export type ChatItem =
    | { kind: 'agent', text: string }
    | { kind: 'customer', text: string }
    | { kind: 'status', text: string }
    | { kind: 'task', text: string }
    | { kind: 'notice', text: string, icon: 'key' | 'calendar' | 'trend' }

export interface FullServiceTab {
    id: string
    label: string
    lead: string
    body: string
    person: {
        name: string
        role: string
        image: string
    }
    duration: number
    chat: Array<ChatItem>
}

export const fullServiceTabs: Array<FullServiceTab> = [
    {
        id: 'requirements',
        label: "Requirements",
        lead: "Most integration work starts as a business process, not a perfect spec.",
        body: " Our solutions engineers ask the right questions, uncover the details that matter, and turn your context into a plan we can build.",
        person: {
            name: "Dana Whitfield",
            role: "Chief Revenue Officer",
            image: '/images/people/dana-whitfield.jpg'
        },
        duration: 15000,
        chat: [
            { kind: 'agent', text: "What kind of workflow are you trying to build?" },
            { kind: 'customer', text: "We need our wholesale customers and price lists from QuickBooks to appear in our WooCommerce B2B store." },
            { kind: 'agent', text: "Got it. QuickBooks is the source of truth and WooCommerce needs it for B2B checkout. Should changes flow one way, or back as well?" },
            { kind: 'customer', text: "One way for now. Orders come later." },
            { kind: 'agent', text: "Perfect. I will scope customers, items and pricing, and keep orders as a separate workflow." },
            { kind: 'status', text: "Connected to QuickBooks" },
            { kind: 'agent', text: "Your sample includes about 3,100 customer records. Are these wholesale buyers who need company accounts?" }
        ]
    },
    {
        id: 'build',
        label: "Build",
        lead: "A dedicated integration engineer builds your workflow on Studio",
        body: " and owns it from first draft to go-live.",
        person: {
            name: "Mateo Ruiz",
            role: "Integration Engineer",
            image: '/images/people/mateo-ruiz.jpg'
        },
        duration: 10000,
        chat: [
            { kind: 'agent', text: "Build update for the QuickBooks to WooCommerce workflow." },
            { kind: 'task', text: "Field mapping complete" },
            { kind: 'task', text: "Edge cases: 14 of 14 passing" },
            { kind: 'task', text: "Test run with your real data: 2,480 records, 0 errors" },
            { kind: 'task', text: "Ready for your review" }
        ]
    },
    {
        id: 'launch',
        label: "Launch and support",
        lead: "After go-live we keep watching.",
        body: " We monitor runs, keep connections fresh, and tune things as you grow.",
        person: {
            name: "Priya Anand",
            role: "Head of Support",
            image: '/images/people/priya-anand.jpg'
        },
        duration: 10000,
        chat: [
            { kind: 'notice', icon: 'key', text: "Heads up: the shipping vendor rotated its credentials. Already renewed, no runs missed." },
            { kind: 'notice', icon: 'calendar', text: "Weekly check-in moved to Thursday." },
            { kind: 'notice', icon: 'trend', text: "Run time down 31% after this month's optimization." }
        ]
    }
]

export interface Testimonial {
    company: string
    quote: string
    name: string
    title: string
    avatar: string
    image: string
    imageAlt: string
    tint: string
}

export const testimonials: Array<Testimonial> = [
    {
        company: "Brightline",
        quote: "FlowCraft rebuilt our wholesale store around our ERP in weeks. About 96% of inbound orders now post without anyone touching them, and returns are fully automatic.",
        name: "Nora Castellane",
        title: "Director of Operations",
        avatar: '/images/people/nora-castellane.jpg',
        image: '/images/testimonials/brightline.jpg',
        imageAlt: "Brightline product photography with bottles on stone pedestals",
        tint: 'rgba(14, 14, 14, 0.78)'
    },
    {
        company: "Kestrel Freight",
        quote: "Fastest turnaround we have ever seen on a systems project, and honestly the best value of anything we evaluated.",
        name: "Elias Brandt",
        title: "Chief Operating Officer",
        avatar: '/images/people/elias-brandt.jpg',
        image: '/images/testimonials/kestrel.jpg',
        imageAlt: "Wide view down the aisles of a Kestrel Freight warehouse",
        tint: 'rgba(5, 32, 18, 0.8)'
    },
    {
        company: "Oakmont Supply",
        quote: "We were re-keying the same data into four systems. FlowCraft connected all of them and the duplicate entry simply stopped.",
        name: "Hana Okafor",
        title: "Director of IT",
        avatar: '/images/people/hana-okafor.jpg',
        image: '/images/testimonials/oakmont.jpg',
        imageAlt: "Workbench with hand tools in the Oakmont Supply workshop",
        tint: 'rgba(14, 14, 14, 0.78)'
    },
    {
        company: "Tessel",
        quote: "FlowCraft has run every integration we have for three years and supplied the platform too. It costs less than the two tools we replaced, and we never had to hire a team to maintain it.",
        name: "Julien Marchetti",
        title: "VP of Information Technology",
        avatar: '/images/people/julien-marchetti.jpg',
        image: '/images/testimonials/tessel.jpg',
        imageAlt: "Espresso machine on the counter of a Tessel coffee bar",
        tint: 'rgba(48, 8, 22, 0.8)'
    },
    {
        company: "Mosaic Health",
        quote: "The support is what stands out. They shaped a custom link between our CRM and accounting system that fit how we actually work.",
        name: "Wren Dalloway",
        title: "Director of Finance",
        avatar: '/images/people/wren-dalloway.jpg',
        image: '/images/testimonials/mosaic.jpg',
        imageAlt: "Interior of a new Mosaic Health clinic under construction",
        tint: 'rgba(14, 14, 14, 0.78)'
    }
]

export interface ActivityPill {
    app: string
    count: number
    label: string
    time: string
}

export const activityRows: Array<Array<ActivityPill>> = [
    [
        { app: "GitHub", count: 62, label: "pull requests created", time: "28m ago" },
        { app: "NetSuite", count: 143, label: "sales orders created", time: "1m ago" },
        { app: "Gusto", count: 8, label: "payrolls created", time: "41s ago" },
        { app: "ShipBob", count: 311, label: "shipments modified", time: "6m ago" },
        { app: "Pipedrive", count: 47, label: "deals modified", time: "2m ago" },
        { app: "WooCommerce", count: 95, label: "products created", time: "1h ago" },
        { app: "QuickBooks Online", count: 19, label: "invoices created", time: "3m ago" },
        { app: "HubSpot", count: 204, label: "contacts modified", time: "1min ago" },
        { app: "Mailchimp", count: 36, label: "campaigns created", time: "14m ago" },
        { app: "Zendesk", count: 71, label: "tickets modified", time: "9m ago" },
        { app: "Shopify", count: 128, label: "customers created", time: "4m ago" },
        { app: "Google Calendar", count: 23, label: "events created", time: "2m ago" },
        { app: "Linear", count: 57, label: "issues modified", time: "36m ago" },
        { app: "Slack", count: 88, label: "messages created", time: "3m ago" },
        { app: "Salesforce", count: 64, label: "opportunities modified", time: "40s ago" }
    ],
    [
        { app: "Stripe", count: 39, label: "payouts reconciled", time: "12m ago" },
        { app: "BigCommerce", count: 152, label: "orders created", time: "1m ago" },
        { app: "Xero", count: 27, label: "bills created", time: "18m ago" },
        { app: "Asana", count: 76, label: "tasks created", time: "1h ago" },
        { app: "Shopify", count: 412, label: "inventory levels updated", time: "5m ago" },
        { app: "ServiceTitan", count: 31, label: "work orders created", time: "22m ago" },
        { app: "Google Sheets", count: 118, label: "rows added", time: "2m ago" },
        { app: "Calendly", count: 53, label: "meetings scheduled", time: "9m ago" },
        { app: "NetSuite", count: 92, label: "return authorizations created", time: "16m ago" },
        { app: "BambooHR", count: 14, label: "employees updated", time: "1h ago" },
        { app: "Airtable", count: 66, label: "records synced", time: "3m ago" },
        { app: "Klaviyo", count: 205, label: "profiles updated", time: "1min ago" },
        { app: "Stripe", count: 38, label: "payments captured", time: "2m ago" },
        { app: "ShipStation", count: 81, label: "fulfillments created", time: "6m ago" },
        { app: "Shopify", count: 44, label: "refunds created", time: "7m ago" }
    ]
]

export const annualFigures = [
    { label: "API calls", value: "3,104,882,415" },
    { label: "Records processed", value: "912.6M" },
    { label: "Time saved", value: "121.4M hours" },
    { label: "Money saved", value: "$5.8B" }
]
